import { useCallback, useEffect, useState } from 'react';
import { Theme, ThemeProvider } from '@emotion/react';
import { ErrorBoundary } from '@sentry/react';
import merge from 'lodash/merge';

import { bridgeAtom, Provider, store, useAtom, useReset, viewedProductsAtom } from '@/store';

import { AppEvents, getProductUrl, parseShopifyUrl } from '@/utils';

import { request } from '@/modules/api';
import { loadTranslations } from '@/modules/app/data';
import { theme as defaultTheme, useEvent } from '@/modules/common';
import { PageType } from '@/modules/models';

import { App } from './App';

import type { ApiAppParams } from 'typings/apiApp';

if (process.env.NODE_ENV === 'production') {
  import('@/utils/sentry');
}

type AppContainerProps = { config: ApiAppParams };

function AppContainer(props: AppContainerProps) {
  const [theme, setTheme] = useState<Theme>(merge({}, defaultTheme, props.config.theme));

  const resetBridgeAtom = useReset(bridgeAtom);
  const [bridge, setBridge] = useAtom(bridgeAtom);
  const [viewedProducts, setViewedProducts] = useAtom(viewedProductsAtom);
  const { pageType, productId } = parseShopifyUrl(window.location.href);
  const isViewed = viewedProducts?.includes(productId);

  const fetchTranslations = useCallback(async () => {
    const { translations } = await loadTranslations(
      props.config.localization ?? 'en',
      props.config.projectName?.toLowerCase()
    );
    return translations ?? {};
  }, [props.config.localization, props.config.projectName]);

  const getBridgeParams = useCallback(
    (config?: Partial<ApiAppParams>) => {
      const {
        apiUrl,
        theme,
        pageType: bridgePageType,
        translations: configTranslations,
        ...others
      } = merge({}, props.config, config);
      request.setBaseUrl(apiUrl);

      setTheme((p) => merge({}, p, theme));

      const url = getProductUrl();
      const { productId, variantId, pageType } = parseShopifyUrl(url);

      fetchTranslations().then((translations) => {
        setBridge((prev) => {
          return merge({}, prev, others, {
            apiUrl,
            pageType: bridgePageType ?? pageType,
            productId,
            variantId,
            translations: merge({}, translations, configTranslations),
          });
        });
      });
    },
    [fetchTranslations, setBridge, props.config]
  );

  const onResetParams = useCallback(() => {
    resetBridgeAtom();
    getBridgeParams();
  }, []);

  const onUpdateParams = useCallback(
    (params: CustomEvent<Partial<ApiAppParams>>) => {
      getBridgeParams(params.detail);
    },
    [getBridgeParams]
  );

  useEffect(() => {
    getBridgeParams();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');

    if (testParam === '1') {
      localStorage.setItem('testUser', '1');
    }
  }, []);

  useEffect(() => {
    if (pageType !== PageType.PRODUCT || isViewed) {
      return;
    }

    setViewedProducts((prevState) => [...prevState, productId]);
  }, [isViewed, pageType, productId, setViewedProducts]);

  useEvent({ name: AppEvents.UPDATE_PARAMS, handler: onUpdateParams });
  useEvent({ name: AppEvents.RESET_CONFIG, handler: onResetParams });

  if (!bridge.pageType || !theme) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function AppContainerProvider(props: AppContainerProps) {
  return (
    <Provider store={store}>
      <AppContainer {...props} />
    </Provider>
  );
}
